import React, { useState, useMemo, useEffect } from 'react';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import ReCaptcha, { REACT_APP_SIGNUP_GOOGLE_SITE_KEY } from './ReCaptcha';
import Link from '@material-ui/core/Link';
import SignupContentWrapper from './SignupContentWrapper';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import { useTranslation } from 'react-i18next';
import { finalStepStyles, useButtonStyles } from './styles';
import { useNavigate } from 'react-router';
import { signupApiDto, calculatePercentage } from '../../../store/signup/types';
import { PATH } from '../../../routes';
import { useInvokeApi } from '../utils/useInvokeApi';
import { useSelector } from 'react-redux';
import { AppState } from '../../../store';
import { PasswordInput } from '../../../theme/PasswordInput';
import { isStrongPassword } from '../utils/validationRegExp';
import { pushEvent } from '../../../utils/clevertapUtils';
import {
    CLICK_USER_PASSWORD_PAGE_SUBMIT_BTN,
    VIEW_USER_PASSWORD_PAGE,
    SIGNUP_SUCCESSFUL,
} from '../../../utils/clevertapEvents';
import { useRouteConfig } from '../utils/useRouteConfig';
import RenderError from '../utils/RenderError';
import { removeUUID } from '../../../utils/localStorage';
import { webConfig } from '../../../webConfig';

const termsLink = webConfig.givelifyTermsLink;

const style = { marginTop: 16 };

const PasswordStep: React.FC = () => {
    const { t } = useTranslation();
    const push = useNavigate();
    const { mainButton, wrapper, buttonProgress } = useButtonStyles();
    const { passwordStep, checkbox, errorStyles } = finalStepStyles();
    const [password, setPassword] = useState<string>('');
    const [passwordError, setPasswordError] = useState<boolean>(false);
    const [authorization, setAuthorization] = useState<boolean>(false);
    const [termsAndCondition, setTermsAndCondition] = useState<boolean>(false);
    const [requestState, makeRequest] = useInvokeApi();
    const { next, previous } = useRouteConfig(PATH.SIGNUP.STEPS.PASSWORD_STEP);

    const signUp = useSelector((state: AppState) => state.Signup);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setPassword(value);
        if (passwordError && isStrongPassword(value)) {
            setPasswordError(false);
        }
    };
    const handleAuthorization = (e: unknown, checked: boolean) =>
        setAuthorization(checked);
    const handleTermsAndCondition = (e: unknown, checked: boolean) =>
        setTermsAndCondition(checked);
    const onSubmit = () => {
        pushEvent(CLICK_USER_PASSWORD_PAGE_SUBMIT_BTN);
        const grecaptcha = (window as any).grecaptcha;
        if (!grecaptcha) return;

        grecaptcha.ready(() => {
            grecaptcha
                .execute(REACT_APP_SIGNUP_GOOGLE_SITE_KEY, { action: 'submit' })
                .then((token: unknown) => {
                    makeRequest(
                        'POST',
                        'signup',
                        signupApiDto({
                            ...signUp,
                            recaptcha: token,
                            userPassword: password,
                        }),
                    );
                });
        });
    };

    useEffect(() => {
        pushEvent(VIEW_USER_PASSWORD_PAGE);
    }, []);
    useEffect(() => {
        if (requestState.type === 'REQUEST_SUCCESS') {
            pushEvent(SIGNUP_SUCCESSFUL, {
                doneeId: signUp.doneeId,
            });
            removeUUID();
            next && push(next);
        }
    }, [requestState, push, next, signUp.doneeId]);

    const copy = useMemo(
        () => ({
            submitButtonText: t('labels.submit'),
            passwordPlaceholder: t('signup.passwordStep.passwordPlaceholder'),
            passwordHyperText: t('signup.passwordStep.passwordHyperText'),
            authorizationText: t('signup.passwordStep.authorizationText'),
            termAndConditions: (
                <span>
                    {t('signup.passwordStep.termAndConditions.first')}&nbsp;
                    <Link href={termsLink} target="_blanks">
                        {t('signup.passwordStep.termAndConditions.last')}
                    </Link>
                    .
                </span>
            ),
        }),
        [t],
    );

    if (calculatePercentage(signUp) !== 100) {
        push(PATH.SIGNUP.STEPS.ONE);
        return null;
    }

    const loading = requestState.type === 'REQUEST_START';
    const isValidPassword = isStrongPassword(password);
    const onBlur = () => {
        setPasswordError(!isValidPassword);
    };

    return (
        <SignupContentWrapper
            heading={t('signup.passwordStep.heading')}
            stepInfo={undefined}
            showCloseButton={true}
            backUrl={previous}
        >
            <div className={passwordStep}>
                <PasswordInput
                    value={password}
                    onBlur={onBlur}
                    error={passwordError}
                    onChange={onChange}
                    placeholder={copy.passwordPlaceholder}
                    helperText={copy.passwordHyperText}
                    className="password-field"
                />
                <ReCaptcha className="reCaptcha" />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={authorization}
                            size="small"
                            onChange={handleAuthorization}
                        />
                    }
                    label={copy.authorizationText}
                    className={checkbox}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={termsAndCondition}
                            size="small"
                            onChange={handleTermsAndCondition}
                        />
                    }
                    label={copy.termAndConditions}
                    className={checkbox}
                />
            </div>
            {requestState.type === 'REQUEST_ERROR' && (
                <RenderError
                    error={requestState.error}
                    className={errorStyles}
                />
            )}
            <div className={wrapper}>
                <Button
                    variant="contained"
                    onClick={onSubmit}
                    disabled={
                        !authorization ||
                        !termsAndCondition ||
                        !isValidPassword ||
                        loading
                    }
                    className={mainButton}
                    style={style}
                >
                    {copy.submitButtonText}
                </Button>
                {loading && (
                    <CircularProgress size={24} className={buttonProgress} />
                )}
            </div>
        </SignupContentWrapper>
    );
};

export default PasswordStep;
